/**
 * Pure decision functions for the odds/market/fixture display lifecycle
 * (lock → grace period → evict, with instant reopen at any point).
 *
 * The backend already sends the exact fields this spec calls for — confirmed
 * against a real captured /sportsnew/fixtures/:id/markets?mode=live response
 * (fixture.mainOdds.{slot}, each market, and each market.odds[] item all
 * carry hideDelayMs / replacementHideDelayMs / flashDurationMs / lockedSince /
 * displayState). These are read first; the spec's own numbers are the
 * fallback for any entity that doesn't carry them.
 *
 * No React/timer code lives here — see evictionScheduler.js for the timer
 * side and SportsnewContext.jsx for how the two are wired together.
 */

import { marketIdentityKey } from './sportsnewDetailPatch.js';
import { isSportsnewActiveLiveStatus } from './sportsnewListFixtureVisibility.js';

export const DEFAULT_HIDE_DELAY_MS = { live: 12000, prematch: 20000 };
export const DEFAULT_MARKET_HIDE_DELAY_MS = { live: 20000, prematch: 20000 };
export const DEFAULT_REPLACEMENT_HIDE_DELAY_MS = { live: 2000, prematch: 3000 };
export const DEFAULT_FLASH_DURATION_MS = 2000;

/** Market lifecycle timers/hidden state must use the exact same identity as
 * raw-market dedupe. A provider marketStateId is a shared state dimension and
 * may legitimately belong to several line fragments, whereas marketGroupId
 * (or the complete fallback composite) identifies one renderable fragment. */
export const marketLifecycleIdentity = marketIdentityKey;

const normalizeMode = (mode) => (mode === 'live' ? 'live' : 'prematch');
const LOCKED_STATUS_RE = /(^|[_\s-])(LOCK|LOCKED|SUSPEND|SUSPENDED|CLOSE|CLOSED|UNAVAILABLE|HIDDEN)([_\s-]|$)/i;
const TERMINAL_FIXTURE_STATES = new Set([
  'FINAL', 'COMPLETED', 'ENDED', 'END', 'FINISHED', 'CANCELLED', 'CANCELED',
  'ABANDONED', 'SETTLED', 'CLOSED',
]);
const TERMINAL_PERIODS = new Set([
  'END-REG', 'END_REG', 'END REG', 'FULL_TIME', 'FULL-TIME', 'FT',
]);
const normalizeToken = (value) => String(value ?? '').trim().toUpperCase();

const entityShapes = (entity) => [entity, entity?.marketState].filter(Boolean);

const hasFalseFlag = (entity, keys) =>
  entityShapes(entity).some((shape) => keys.some((key) => shape?.[key] === false));

const hasTrueFlag = (entity, keys) =>
  entityShapes(entity).some((shape) => keys.some((key) => shape?.[key] === true));

const isLockedStatusText = (value) => (
  typeof value === 'string' && LOCKED_STATUS_RE.test(value.trim())
);

export const resolveHideDelayMs = (entity, mode) =>
  entity?.hideDelayMs
    ?? (entity?.scope === 'market' || entity?.entityScope === 'market'
      ? DEFAULT_MARKET_HIDE_DELAY_MS[normalizeMode(mode)]
      : DEFAULT_HIDE_DELAY_MS[normalizeMode(mode)]);

export const resolveReplacementHideDelayMs = (entity, mode) =>
  entity?.replacementHideDelayMs ?? DEFAULT_REPLACEMENT_HIDE_DELAY_MS[normalizeMode(mode)];

export const resolveFlashDurationMs = (entity) =>
  entity?.flashDurationMs ?? DEFAULT_FLASH_DURATION_MS;

/** Is this odd/market currently locked/unbettable? Covers every shape seen
 * across list patches, fixture patches, and the REST markets response. */
export const isEntityLocked = (entity) => {
  if (!entity) return true;
  if (hasFalseFlag(entity, ['bettable', 'isBettable', 'is_bettable'])) return true;
  if (hasTrueFlag(entity, ['suspended', 'isSuspended', 'is_suspended', 'locked'])) return true;
  if (entityShapes(entity).some((shape) => (
    isLockedStatusText(shape.state)
    || isLockedStatusText(shape.displayState)
    || isLockedStatusText(shape.reason)
    || isLockedStatusText(shape.lockReason)
  ))) return true;
  return false;
};

/** If the backend says this entity has already been locked for a while
 * (lockedSince/firstLockedAt), the grace period may already be partially or
 * fully elapsed — e.g. on initial page load, or after a reconnect recovery.
 * Returns the remaining ms to schedule for (0 if already overdue, meaning
 * "evict on next tick" rather than "evict immediately inline"). */
export const remainingHideDelayMs = (entity, mode, now = Date.now()) => {
  const totalDelay = resolveHideDelayMs(entity, mode);
  const lockedSince = entity?.lockedSince ?? entity?.firstLockedAt ?? null;
  if (!lockedSince) return totalDelay;
  const lockedAt = new Date(lockedSince).getTime();
  if (Number.isNaN(lockedAt)) return totalDelay;
  return Math.max(0, totalDelay - (now - lockedAt));
};

/** Extract the odds/selections array from a market regardless of which key
 * the payload used (selections vs odds — both are observed in the wild). */
export const marketOdds = (market) => market?.selections ?? market?.odds ?? [];

/** A market's own `bettable`/`suspended` may already summarize its children,
 * but don't trust that alone — some payloads only set it at the top level
 * lazily. A market is "fully locked" only when EVERY one of its odds is
 * locked (an empty market is not considered locked — nothing to lock). */
export const isMarketFullyLocked = (market) => {
  const odds = marketOdds(market);
  if (odds.length === 0) return false;
  return odds.every((o) => isEntityLocked(o));
};

export const isTerminalFixtureSnapshot = (fixture) => {
  if (!fixture) return false;
  const status = normalizeToken(
    fixture.status ?? fixture.fixtureStatus ?? fixture.state ?? fixture.lifecycleStatus,
  );
  const period = normalizeToken(fixture.period ?? fixture.periodStatus ?? fixture.matchPeriod);
  return TERMINAL_FIXTURE_STATES.has(status) || TERMINAL_PERIODS.has(period);
};

export const shouldPreserveLiveAllLockedDetail = ({
  fixture,
  markets,
  mode,
  operatorSuspended = false,
} = {}) => {
  if (normalizeMode(mode) !== 'live' || operatorSuspended || !fixture) return false;
  if (fixture.stale === true || fixture.isStale === true || fixture.is_stale === true) return false;
  if (isTerminalFixtureSnapshot(fixture)) return false;
  const status = normalizeToken(
    fixture.status ?? fixture.fixtureStatus ?? fixture.state ?? fixture.lifecycleStatus,
  );
  const rows = Array.isArray(markets) ? markets : [];
  return isSportsnewActiveLiveStatus(status)
    && rows.length > 0
    && rows.every((market) => marketOdds(market).length > 0 && isMarketFullyLocked(market));
};

export const cancelLiveAllLockEvictions = ({
  markets,
  oddScheduler,
  marketScheduler,
} = {}) => {
  const oddIds = [];
  const marketIds = [];
  (Array.isArray(markets) ? markets : []).forEach((market, marketIndex) => {
    const marketId = marketLifecycleIdentity(market) ?? `idx:${marketIndex}`;
    marketScheduler?.cancel?.(`market:${marketId}`);
    marketIds.push(marketId);
    marketOdds(market).forEach((odd) => {
      if (odd?.oddStateId == null) return;
      oddScheduler?.cancel?.(`odd:${odd.oddStateId}`);
      oddIds.push(odd.oddStateId);
    });
  });
  return Object.freeze({ oddIds, marketIds });
};

/**
 * Fixture membership is not selection availability. A fresh live fixture
 * stays on screen while every market is locked; lifecycle/operator authority
 * removes the fixture through the list contract instead. Prematch keeps the
 * existing no-bettable eviction behavior.
 */
export const shouldEvictFixtureForAvailability = (fixture, mode) => (
  normalizeMode(mode) !== 'live'
  && (isEntityLocked(fixture?.marketState) || fixture?.marketCount === 0)
);

/** Display-only pair slots are UI coherence rows, not provider lifecycle rows.
 * They must survive normal locked-odd eviction so a one-sided handicap/total
 * keeps its opposite side visible as a locked placeholder. */
export const isDisplayOnlyPairSlot = (odd) => {
  if (!odd) return false;
  return odd.displayOnly === true
    || odd.syntheticUnavailable === true
    || odd.reason === 'PAIR_SLOT_UNAVAILABLE'
    || odd.lockReason === 'PAIR_SLOT_UNAVAILABLE';
};

/** Given one odd that just became locked, find a sibling in the SAME market
 * that represents a "replacement line" for it — i.e. shares the pair/line
 * grouping but is a DIFFERENT, currently-active line. This is what
 * distinguishes "the 2.5 total just moved to 3.0" (fast replacement-hide)
 * from "this selection just went stale with nothing replacing it" (falls
 * back to the standard, longer hide delay). Matches on canonicalPairKey /
 * lineGroupId (both confirmed fields), excluding the odd's own id/line so it
 * doesn't match itself. */
export const findReplacementOdd = (lockedOdd, market) => {
  if (!lockedOdd) return null;
  const groupKey = lockedOdd.lineGroupId ?? lockedOdd.canonicalPairKey ?? null;
  if (!groupKey) return null;
  const odds = marketOdds(market);
  return odds.find((o) => {
    if (o === lockedOdd || o.oddStateId === lockedOdd.oddStateId) return false;
    const otherGroupKey = o.lineGroupId ?? o.canonicalPairKey ?? null;
    if (otherGroupKey !== groupKey) return false;
    const otherLine = o.canonicalLineKey ?? o.lineKey ?? o.line ?? null;
    const lockedLine = lockedOdd.canonicalLineKey ?? lockedOdd.lineKey ?? lockedOdd.line ?? null;
    if (otherLine != null && lockedLine != null && otherLine === lockedLine) return false; // same line, not a replacement
    return !isEntityLocked(o); // must actually be active to count as a replacement
  }) ?? null;
};
